import { useEffect, useState } from "react";
import { X, Loader2, Scale, CheckCircle2, XCircle, AlertTriangle, Download, Save } from "lucide-react";
import api, { API } from "@/lib/api";
import { RaiseButton } from "@/components/RaiseToAdmin";
import FinalisationPanel from "./FinalisationPanel";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const token = () => localStorage.getItem("mdau_token");

const Icon = ({ severity }) =>
  severity === "pass" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
    : severity === "fail" ? <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
      : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />;

const DECISION_STYLE = {
  pending: "border-white/20 text-white/50", approved: "border-emerald-400/50 text-emerald-300",
  reduced: "border-amber-400/50 text-amber-300", rejected: "border-red-400/50 text-red-300",
};

export default function AssessmentModal({ claimId, onClose }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");
  const [err, setErr] = useState("");
  const [view, setView] = useState("scope");
  const [fin, setFin] = useState(null);
  const [finDirty, setFinDirty] = useState({});
  const [finOncost, setFinOncost] = useState({});

  const loadFin = () => api.get(`/fieldclaim/claims/${claimId}/finalisation`).then((r) => setFin(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/fieldclaim/claims/${claimId}/assessment`).then((r) => {
      setData(r.data);
      const init = {};
      r.data.assets.forEach((a) => { init[a.id] = { decision: a.decision, approved_amount: a.approved_amount, reason: a.reason }; });
      setLines(init);
    }).catch(() => setErr("Could not load the assessment."));
    loadFin();
  }, [claimId]);

  const onFinLine = (lineId, patch) => {
    setFinDirty((s) => ({ ...s, [lineId]: patch }));
    setFin((f) => ({ ...f, measures: f.measures.map((g) => ({ ...g, lines: g.lines.map((x) => x.id === lineId ? { ...x, ...patch } : x) })) }));
  };
  const onFinBulk = (patches) => {
    setFinDirty((s) => {
      const next = { ...s };
      patches.forEach((p) => { next[p.id] = { status: p.status, reason: p.reason }; });
      return next;
    });
    const byId = Object.fromEntries(patches.map((p) => [p.id, p]));
    setFin((f) => ({ ...f, measures: f.measures.map((g) => ({ ...g, lines: g.lines.map((x) => byId[x.id] ? { ...x, status: byId[x.id].status, reason: byId[x.id].reason } : x) })) }));
  };
  const onFinOncost = (patch) => { setFinOncost((s) => ({ ...s, ...patch })); setFin((f) => ({ ...f, ...patch })); };

  const saveFin = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/fieldclaim/claims/${claimId}/finalisation`, {
        reviews: Object.entries(finDirty).map(([line_id, p]) => ({ line_id, status: p.status, reason: p.reason || "" })),
        ...finOncost,
      });
      setFinDirty({}); setFinOncost({});
      await loadFin();
      setSaved(new Date().toISOString());
    } catch { setErr("Could not save the finalisation."); }
    finally { setBusy(false); }
  };

  const exportFin = () => {
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/finalisation?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const setLine = (id, patch) => { setSaved(""); setLines((s) => ({ ...s, [id]: { ...s[id], ...patch } })); };

  const decide = (a, decision) => {
    const claimed = a.claimed || 0;
    setLine(a.id, { decision, approved_amount: decision === "approved" ? claimed : decision === "rejected" ? 0 : lines[a.id]?.approved_amount ?? claimed });
  };

  const totals = () => {
    if (!data) return { claimed: 0, approved: 0, inel: 0, pct: 0 };
    let claimed = 0, approved = 0;
    data.assets.forEach((a) => {
      const l = lines[a.id] || {};
      const c = a.claimed || 0;
      claimed += c;
      approved += l.approved_amount != null ? Number(l.approved_amount) : (l.decision === "rejected" ? 0 : c);
    });
    const inel = Math.max(claimed - approved, 0);
    return { claimed, approved, inel, pct: claimed ? (inel / claimed) * 100 : 0 };
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { lines: data.assets.map((a) => ({ asset_id: a.id, ...lines[a.id], approved_amount: lines[a.id]?.approved_amount != null ? Number(lines[a.id].approved_amount) : null })) };
      const { data: r } = await api.put(`/fieldclaim/claims/${claimId}/assessment`, body);
      setSaved(r.updated_at);
    } catch { setErr("Could not save the assessment."); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = `${API}/fieldclaim/claims/${claimId}/export/assessment?token=${token()}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const t = totals();

  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="assessment-modal"
        className="bg-navy-900 border border-gold-500/30 w-full max-w-5xl p-6 space-y-5 max-h-[92vh] overflow-y-auto text-white">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl flex items-center gap-2"><Scale className="h-5 w-5 text-gold-400" /> Assessor mode</h2>
          <div className="flex items-center gap-4">
            <RaiseButton contextTitle={`Assessor mode — ${data?.claim?.name || claimId}`} />
            <button onClick={onClose} data-testid="assessment-modal-close"><X className="h-5 w-5 text-white/60" /></button>
          </div>
        </div>

        <div className="flex gap-2">
          {[["scope", "Scope determinations"], ["finalisation", "Ledger finalisation — EW / IRW / EPAR"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`assess-tab-${k}`}
              className={`border px-4 py-2 text-xs transition-colors ${view === k ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/50 hover:text-gold-300"}`}>
              {label}
            </button>
          ))}
        </div>

        {view === "finalisation" && (
          <>
            <FinalisationPanel data={fin} onLine={onFinLine} onOncost={onFinOncost} onBulk={onFinBulk} />
            {fin?.has_ledger && (
              <div className="flex items-center justify-end gap-2">
                {saved && <Mono className="text-[0.5rem] text-emerald-300">Saved</Mono>}
                <button onClick={saveFin} disabled={busy} data-testid="finalisation-save"
                  className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-4 py-2 text-sm font-medium hover:bg-gold-400 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save finalisation
                </button>
                <button onClick={exportFin} data-testid="finalisation-export"
                  className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 px-4 py-2 text-sm hover:bg-emerald-400/10">
                  <Download className="h-4 w-4" /> Finalisation CSV
                </button>
              </div>
            )}
          </>
        )}

        {view === "scope" && err && <div className="text-red-300 text-sm" data-testid="assessment-error">{err}</div>}
        {view === "scope" && !data && !err && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Running assessment intelligence…</div>}

        {view === "scope" && data && (
          <>
            <div className="border border-gold-500/20 bg-navy-950/50 p-4 space-y-3" data-testid="assessment-checks">
              <Mono className="text-[0.55rem] text-gold-400">Assessment intelligence — automated checks</Mono>
              {data.checks.map((c) => (
                <div key={c.code} className="flex items-start gap-2.5" data-testid={`assess-check-${c.code}`}>
                  <Icon severity={c.severity} />
                  <div className="min-w-0">
                    <span className="text-sm">{c.label}{c.amount ? <span className="font-mono text-xs text-gold-300 ml-2">${Number(c.amount).toLocaleString()}</span> : null}</span>
                    <p className="text-white/45 font-light text-xs mt-0.5">{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead><tr className="border-b border-gold-500/20 text-left">
                  {["Asset", "Measure", "Claimed", "Benchmark", "Var", "Decision", "Approved", "Reason"].map((h) => (
                    <th key={h} className="py-2 pr-3"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.assets.map((a) => {
                    const l = lines[a.id] || {};
                    return (
                      <tr key={a.id} className="border-b border-white/5 align-top" data-testid={`assess-line-${a.id}`}>
                        <td className="py-2.5 pr-3"><span className="text-white">{a.name}</span><span className="text-white/35 text-xs block">{a.site_number}</span></td>
                        <td className="py-2.5 pr-3 font-mono text-xs text-gold-300">{a.funding_measure}</td>
                        <td className="py-2.5 pr-3 font-mono text-xs">{money(a.claimed)}</td>
                        <td className="py-2.5 pr-3 font-mono text-xs text-white/50">{money(a.cet_cost)}</td>
                        <td className={`py-2.5 pr-3 font-mono text-xs ${a.variance_pct > 20 ? "text-amber-300" : "text-white/50"}`}>{a.variance_pct != null ? `${a.variance_pct}%` : "—"}</td>
                        <td className="py-2.5 pr-3">
                          <select value={l.decision || "pending"} onChange={(e) => decide(a, e.target.value)}
                            data-testid={`assess-decision-${a.id}`}
                            className={`bg-navy-950 border rounded-sm px-2 py-1.5 text-xs ${DECISION_STYLE[l.decision || "pending"]}`}>
                            {["pending", "approved", "reduced", "rejected"].map((d) => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </td>
                        <td className="py-2.5 pr-3">
                          <input type="number" value={l.approved_amount ?? ""} placeholder={a.claimed ?? ""}
                            onChange={(e) => setLine(a.id, { approved_amount: e.target.value === "" ? null : e.target.value })}
                            data-testid={`assess-amount-${a.id}`}
                            className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1.5 text-xs w-28 font-mono focus:outline-none focus:border-gold-500/60" />
                        </td>
                        <td className="py-2.5">
                          <input value={l.reason || ""} onChange={(e) => setLine(a.id, { reason: e.target.value })}
                            placeholder="Reason / condition…" data-testid={`assess-reason-${a.id}`}
                            className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1.5 text-xs w-full min-w-[160px] focus:outline-none focus:border-gold-500/60" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="border border-gold-500/25 bg-navy-950/50 p-4 flex flex-wrap items-center justify-between gap-4" data-testid="assessment-summary">
              <div className="flex flex-wrap gap-6 text-sm">
                <span>Claimed <span className="font-mono text-white block">{money(t.claimed)}</span></span>
                <span>Approved <span className="font-mono text-emerald-300 block">{money(t.approved)}</span></span>
                <span>Ineligible <span className="font-mono text-amber-300 block">{money(t.inel)}</span></span>
                <span>Tolerance
                  <span className={`font-mono block ${t.pct <= 5 ? "text-emerald-300" : "text-red-300"}`} data-testid="assessment-tolerance">
                    {t.pct.toFixed(2)}% {t.pct <= 5 ? "· within 5% — proceed" : "· exceeds 5% — escalate"}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {saved && <Mono className="text-[0.5rem] text-emerald-300">Saved {saved.slice(11, 16)} UTC</Mono>}
                <button onClick={save} disabled={busy} data-testid="assessment-save"
                  className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-4 py-2 text-sm font-medium hover:bg-gold-400 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save assessment
                </button>
                <button onClick={exportCsv} data-testid="assessment-export"
                  className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 px-4 py-2 text-sm hover:bg-emerald-400/10">
                  <Download className="h-4 w-4" /> Determination CSV
                </button>
              </div>
            </div>
            <p className="text-white/30 text-[0.65rem] font-light">
              Export is generated on demand — no portal windows. Save first so the determination CSV carries your latest decisions.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
